import type { DragEvent } from 'react';
import { cn } from '@immo/ui-kit';
import type { LandIqBoardColumn, LandIqBoardTicket, LandIqDataRunMetrics } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { BoardCard } from '@/components/landiq/board/board-card';

export const COUNT_PILL_CLASS =
  'inline-flex h-5 min-w-[20px] items-center justify-center rounded-pill bg-subtle px-1.5 text-sm font-medium tabular-nums text-text-tertiary';

export type DropState = 'idle' | 'valid' | 'hovered' | 'invalid';

const COLUMN_STATE_CLASS: Record<DropState, string> = {
  idle: '',
  valid: 'ring-1 ring-inset ring-interactive/50',
  hovered: 'ring-2 ring-inset ring-interactive bg-interactive-subtle-bg/50',
  invalid: 'opacity-40',
};

type Props = {
  readonly column: LandIqBoardColumn;
  readonly tickets: ReadonlyArray<LandIqBoardTicket>;
  readonly runMetrics: Readonly<Record<string, LandIqDataRunMetrics>>;
  readonly dropState: DropState;
  readonly dragging: string | undefined;
  readonly isDraggable: (ticket: LandIqBoardTicket) => boolean;
  readonly onHover: (column: LandIqBoardColumn | null) => void;
  readonly onDrop: (column: LandIqBoardColumn) => void;
  readonly onDragStart: (ticket: LandIqBoardTicket) => void;
  readonly onDragEnd: () => void;
};

export function BoardColumn({
  column,
  tickets,
  runMetrics,
  dropState,
  dragging,
  isDraggable,
  onHover,
  onDrop,
  onDragStart,
  onDragEnd,
}: Props) {
  const t = useTranslations('pages.admin.landiq-data-runs');
  const name = t(`board.columns.${column}`);

  // Every column but the dragged card's own takes the drop, dimmed ones too: a drop elsewhere shows its message.
  const accepts = dropState !== 'idle';

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    if (!accepts) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    onHover(column);
  };

  const handleDragLeave = (event: DragEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;

    onHover(null);
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    onDrop(column);
  };

  return (
    <section
      aria-label={name}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn('flex min-w-0 flex-col gap-2 rounded-md bg-canvas p-2 transition', COLUMN_STATE_CLASS[dropState])}
    >
      <h2 className="flex items-center gap-2 text-base-sm font-semibold text-text-primary">
        {name}
        <span className={COUNT_PILL_CLASS}>{tickets.length}</span>
      </h2>
      {tickets.length === 0 ? (
        <div className="h-16 rounded-lg border border-dashed border-border" aria-hidden />
      ) : (
        <ul className="flex flex-col gap-2">
          {tickets.map((ticket) => (
            <BoardCard
              key={ticket.ticket}
              ticket={ticket}
              metrics={runMetrics[ticket.openEntryId] ?? null}
              draggable={isDraggable(ticket)}
              dragging={dragging === ticket.ticket}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
